import { Platform } from 'react-native';
import * as FileSystem from '@/lib/fileSystemLegacy';
import { ref, uploadBytes, uploadString } from 'firebase/storage';
import { auth, storage } from '@/lib/firebase';
import { syncQueue, type SyncAction } from './syncQueue';
import { finalizeDocumentUpload, prepareDocumentUpload, type DocumentMetadata, type DocumentType } from './documentVault';
import {
  DOCUMENT_ALLOWED_EXTENSIONS,
  DOCUMENT_ALLOWED_MIME_TYPES,
  MAX_DOCUMENT_SIZE_BYTES,
  hasAllowedMimeOrExtension,
} from './uploadPolicy';

const PENDING_UPLOAD_DIR = 'tripflow_pending_doc_uploads/';

const BASE64_ENCODING =
  typeof FileSystem !== 'undefined' && FileSystem.EncodingType?.Base64 != null
    ? FileSystem.EncodingType.Base64
    : ('base64' as const);

/**
 * Copy a picked file into app document storage so it survives cache eviction before sync runs.
 * No-op on web (caller keeps the blob URI).
 */
export async function copyUriToPersistentPendingUpload(sourceUri: string, fileName: string): Promise<string> {
  if (Platform.OS === 'web') {
    return sourceUri;
  }
  const base = FileSystem.documentDirectory;
  if (!base) {
    throw new Error('Document storage is not available on this device.');
  }
  const dir = `${base}${PENDING_UPLOAD_DIR}`;
  await FileSystem.makeDirectoryAsync(dir, { intermediates: true });
  const safe = fileName.replace(/\s+/g, '_').replace(/[^\w.\-]+/g, '_');
  const targetUri = `${dir}${Date.now()}_${safe}`;

  try {
    await FileSystem.copyAsync({ from: sourceUri, to: targetUri });
  } catch (copyErr) {
    console.warn('[DocumentUploadQueue] copyAsync failed, using base64 read/write:', copyErr);
    const b64 = await FileSystem.readAsStringAsync(sourceUri, { encoding: BASE64_ENCODING });
    await FileSystem.writeAsStringAsync(targetUri, b64, { encoding: BASE64_ENCODING });
  }

  const verify = await FileSystem.getInfoAsync(targetUri);
  if (!verify.exists || (typeof verify.size === 'number' && verify.size <= 0)) {
    throw new Error('Could not keep a copy of this file on the device for upload. Try again while online.');
  }

  return targetUri;
}

function isPersistentPendingUploadUri(uri: string): boolean {
  return uri.includes(PENDING_UPLOAD_DIR);
}

type UploadDocumentPayload = {
  fileUri: string;
  fileName: string;
  documentName?: string;
  contentType: string;
  documentType: DocumentType;
  expiresOn?: string;
  metadata?: DocumentMetadata;
};

export type SaveManualDocumentPayload = {
  name: string;
  type: DocumentType;
  expiresOn?: string;
  metadata?: DocumentMetadata;
  parseStatus?: string;
  ocrRawText?: string | null;
  fieldConfidence?: Record<string, number>;
  parseWarnings?: string[];
  missingRequiredFields?: string[];
  detectedDocumentType?: string | null;
  ocrEngine?: string | null;
  extractionSource?: string | null;
};

export async function queueDocumentUpload(payload: UploadDocumentPayload): Promise<void> {
  await syncQueue.queueAction({
    type: 'upload_document',
    payload,
  });
}

export async function queueManualDocumentSave(payload: SaveManualDocumentPayload): Promise<void> {
  await syncQueue.queueAction({
    type: 'save_manual_document',
    payload,
  });
}

syncQueue.registerHandler('upload_document', async (action: SyncAction) => {
  const payload = action.payload as UploadDocumentPayload;

  if (!payload?.fileUri || !payload.fileName || !payload.contentType) {
    throw new Error('Invalid upload payload');
  }

  const fileInfo = await FileSystem.getInfoAsync(payload.fileUri);
  if (!fileInfo.exists) {
    throw new Error('Cached file is no longer available for upload');
  }
  if (typeof fileInfo.size === 'number' && fileInfo.size > MAX_DOCUMENT_SIZE_BYTES) {
    throw new Error('File exceeds 10MB limit');
  }
  if (!hasAllowedMimeOrExtension(payload.contentType, payload.fileName, DOCUMENT_ALLOWED_MIME_TYPES, DOCUMENT_ALLOWED_EXTENSIONS)) {
    throw new Error('File type not supported. Allowed types: PDF, JPG, PNG, DOC, DOCX');
  }

  const { storagePath, uploadUrl } = await prepareDocumentUpload(payload.fileName, payload.contentType);
  const currentUid = auth.currentUser?.uid;

  if (uploadUrl) {
    if (Platform.OS === 'web') {
      const response = await fetch(payload.fileUri);
      if (!response.ok) {
        throw new Error('Could not read file for upload');
      }
      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('File is empty or could not be read for upload');
      }
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': payload.contentType },
        body: blob,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed (${uploadResponse.status})`);
      }
    } else {
      const uploadResult = await FileSystem.uploadAsync(uploadUrl, payload.fileUri, {
        httpMethod: 'PUT',
        headers: { 'Content-Type': payload.contentType },
        uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
      });
      if (uploadResult.status < 200 || uploadResult.status >= 300) {
        throw new Error(`Upload failed (${uploadResult.status})`);
      }
    }
  } else {
    // Older backend responses may not include signed uploadUrl.
    // Fall back to Firebase SDK upload using storagePath.
    const storageRef = ref(storage, storagePath);
    if (Platform.OS === 'web') {
      const response = await fetch(payload.fileUri);
      if (!response.ok) {
        throw new Error('Could not read file for upload');
      }
      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('File is empty or could not be read for upload');
      }
      await uploadBytes(storageRef, blob, {
        contentType: payload.contentType,
        customMetadata: currentUid ? { uploadedBy: currentUid } : undefined,
      });
    } else {
      const encoding =
        FileSystem.EncodingType?.Base64 != null ? FileSystem.EncodingType.Base64 : ('base64' as const);
      const base64Data = await FileSystem.readAsStringAsync(payload.fileUri, { encoding });
      const cleaned = base64Data.replace(/\s/g, '');
      if (!cleaned.length) {
        throw new Error('File is empty or could not be read for upload');
      }
      await uploadString(storageRef, cleaned, 'base64', {
        contentType: payload.contentType,
        customMetadata: currentUid ? { uploadedBy: currentUid } : undefined,
      });
    }
  }

  await finalizeDocumentUpload({
    name: payload.documentName?.trim() || payload.fileName,
    type: payload.documentType,
    entryType: 'upload',
    storagePath,
    expiresOn: payload.expiresOn ? new Date(payload.expiresOn) : undefined,
    metadata: payload.metadata,
    encryptionMethod: 'none',
  });

  // Only remove the staged copy after a full success. A previous `finally` here deleted the file on
  // every attempt, so the first transient failure (network/auth) left retries with "file missing".
  if (Platform.OS !== 'web' && isPersistentPendingUploadUri(payload.fileUri)) {
    try {
      await FileSystem.deleteAsync(payload.fileUri, { idempotent: true });
    } catch {
      // best-effort cleanup
    }
  }
});

syncQueue.registerHandler('save_manual_document', async (action: SyncAction) => {
  const payload = action.payload as SaveManualDocumentPayload;

  if (!payload?.name?.trim() || !payload?.type) {
    throw new Error('Invalid manual document payload');
  }

  await finalizeDocumentUpload({
    name: payload.name.trim(),
    type: payload.type,
    entryType: 'manual',
    storagePath: null,
    expiresOn: payload.expiresOn ? new Date(payload.expiresOn) : undefined,
    metadata: payload.metadata,
    encryptionMethod: 'none',
    parseStatus: payload.parseStatus,
    ocrRawText: payload.ocrRawText,
    fieldConfidence: payload.fieldConfidence,
    parseWarnings: payload.parseWarnings,
    missingRequiredFields: payload.missingRequiredFields,
    detectedDocumentType: payload.detectedDocumentType,
    ocrEngine: payload.ocrEngine,
    extractionSource: payload.extractionSource,
  });
});
